import Button from "../../../components/form/button";
import Container from "../../../components/Container";
import { Helmet } from "react-helmet";
import { Input } from "../../../components/ui/input";
import { useEffect, useState } from "react";
import Multiselect from "../../../components/form/multiselect";
import List, { ListObject } from "../../../components/form/List";
import { createSlug } from "../../../lib/utils";
import { IRecipeRecord, Status } from "../../../lib/types";
import ErrorMessage from "../../../components/form/errorMessage";
import pb from "../../../lib/pocketbase";
import { useToast } from "../../../components/ui/use-toast";
import { RecipeTags } from "../../../lib/consts";
import { Link, useNavigate, useParams } from "react-router-dom";
import MarkdownEditor from "../../../components/form/MarkdownEditor";
import { Switch } from "../../../components/ui/switch";
import { config } from "../../../config";

interface RecipeData {
    title: string;
    slug: string;
    servings: string;
    tags: string[];
    ingredients: ListObject[];
    recipe: string;
    user?: string;
    image: File | null;
    public: boolean;
    duration: string;
}

export default function EditRecipe() {
    const id = useParams().id;
    const navigate = useNavigate();
    const [data, setData] = useState<RecipeData>({
        title: "",
        slug: "",
        servings: "",
        tags: [],
        ingredients: [],
        recipe: "",
        user: pb.authStore?.model?.id,
        image: null,
        public: false,
        duration: ""
    });
    const [savingStatus, setSavingStatus] = useState<Status>("none");
    const [error, setError] = useState<string>("");
    const [recipe, setRecipe] = useState<IRecipeRecord>();
    const { toast } = useToast();

    async function load() {
        if (!id) return;
        const recipe: IRecipeRecord = await pb.collection("recipe").getOne(id, {
            expand: "ingredients"
        });
        setRecipe(recipe);

        setData({
            title: recipe.title,
            slug: recipe.slug,
            servings: recipe.servings,
            tags: recipe.tags,
            ingredients: recipe.ingredients.length
                ? recipe.expand?.ingredients?.map((i: any) => {
                      return { key: i.id, value: i.ingredient };
                  })
                : [],
            recipe: recipe.recipe,
            user: pb.authStore?.model?.id,
            image: null,
            public: recipe.public,
            duration: recipe.duration
        });
    }

    useEffect(() => {
        load();
    }, []);

    async function Save() {
        setSavingStatus("loading");
        const fd = new FormData();
        if (!id) {
            setSavingStatus("error");
            setError("Id is not set.");
            return;
        }
        if (!data.user) {
            setSavingStatus("error");
            setError("You are no longer logged in.");
            return;
        }
        if (data.title.length <= 2) {
            setSavingStatus("error");
            setError("Recipe title must be longer than 2 characters");
            return;
        }
        data.slug = createSlug(data.title);
        if (!data.servings) {
            setSavingStatus("error");
            setError("Servings must be declared");
            return;
        }
        if (!data.recipe) {
            setSavingStatus("error");
            setError("Recipe must be declared");
            return;
        }

        const ingredients: string[] = [];

        await Promise.all(
            data.ingredients.map(async (ingredient) => {
                if (!ingredient.key) {
                    const record = await pb.collection("ingredient").create({
                        ingredient: ingredient.value
                    });
                    ingredients.push(record.id);
                    return;
                } else {
                    await pb.collection("ingredient").update(ingredient.key, {
                        ingredient: ingredient.value
                    });
                }
                ingredients.push(ingredient.key);
            })
        );
        //todo: loop over the original ingredients, if they are no longer in the ingredients we delete them.
        if (recipe?.ingredients) {
            for (let ingredient of recipe?.ingredients) {
                if (!ingredients.includes(ingredient)) {
                    await pb.collection("ingredient").delete(ingredient);
                }
            }
        }

        fd.append("user", data.user);
        fd.append("title", data.title);
        fd.append("public", JSON.stringify(data.public));
        fd.append("slug", data.slug);
        fd.append("recipe", data.recipe);
        fd.append("servings", data.servings);
        fd.append("duration", data.duration);
        data.tags.map((tag) => {
            fd.append("tags", tag);
        });
        ingredients.map((ingredient) => {
            fd.append("ingredients", ingredient);
        });
        if (data.image) fd.append("image", data.image);

        await pb
            .collection("recipe")
            .update(id, fd)
            .then(() => {
                navigate(-1);
                toast({
                    title: `👍 Updated!`,
                    description: `I have updated your recipe!'`
                });
                setSavingStatus("success");
            })
            .catch(() => setSavingStatus("error"));
    }

    function onFieldChange(e: React.ChangeEvent<HTMLInputElement>) {
        setData((oldData) => {
            return {
                ...oldData,
                [e.target.id]: e.target.value
            };
        });
    }

    function remove() {
        const remove = window.confirm("Are you sure you want to delete this?");

        if (remove && id) {
            pb.collection("recipe").delete(id);
            recipe?.ingredients?.map((ingredientId) => {
                pb.collection("ingredient").delete(ingredientId);
            });
            toast({
                title: `👍 Deleted!`,
                description: `I have deleted the recipe!`
            });
            navigate("/studio/cookbook");
        }
    }

    useEffect(() => {
        if (savingStatus === "error") {
            setTimeout(() => {
                setSavingStatus("none");
            }, 2000);
        }
    }, [savingStatus]);

    return (
        <>
            <Helmet>
                <title>ReinVB Studio — Edit recipe</title>
            </Helmet>

            <Container className="my-4 md:my-8 lg:my-16">
                <div className="flex items-center justify-between">
                    <h2 className="text-3xl font-bold">Edit recipe.</h2>
                    <Button onClick={remove} size={"small"}>
                        Delete
                    </Button>
                </div>
                <ErrorMessage error={error} close={() => setError("")} className="mt-4" />
                <div className="mt-8 flex flex-col gap-6">
                    <div className="flex flex-col gap-2">
                        <label htmlFor="title" className="font-semibold">
                            Name of the recipe *
                        </label>
                        <Input value={data.title} id="title" type="text" onChange={(e) => onFieldChange(e)} placeholder="Cupcakes" />
                        {data.title && <p className="text-sm">slug: {createSlug(data.title)}</p>}
                    </div>
                    <div className="flex items-center gap-4">
                        <label htmlFor="public" className="font-semibold">
                            Public
                        </label>
                        <Switch id="public" checked={data.public} onCheckedChange={(check) => setData({ ...data, public: check })} />
                    </div>
                    {data.public && (
                        <div className="flex items-center gap-2 rounded bg-slate-100 px-4 py-2 text-slate-500 dark:bg-slate-800 dark:text-slate-500">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                            </svg>
                            <Link target="_blank" to={`${config.base}/recipe/${data.slug}/${id}`}>{`${config.base}/recipe/${data.slug}/${id}`}</Link>
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <label htmlFor="servings" className="font-semibold">
                            Servings *
                        </label>
                        <Input value={data.servings} id="servings" type="text" onChange={(e) => onFieldChange(e)} placeholder="4 persons / 12 cupcakes..." />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label htmlFor="duration" className="font-semibold">
                            Duration
                        </label>
                        <Input value={data.duration} id="duration" type="text" onChange={(e) => onFieldChange(e)} placeholder="12 minutes" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="font-semibold">Tags</label>
                        <Multiselect options={RecipeTags} selected={data.tags} placeholder="Select your tags" onChange={(selected) => setData({ ...data, tags: selected })} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="font-semibold">Ingredients</label>
                        <List placeholder="Add new ingredient" list={data.ingredients} onChange={(list) => setData({ ...data, ingredients: list })} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label htmlFor="image" className="font-semibold">
                            Image
                        </label>
                        <Input
                            className="file:text-slate-400 dark:file:text-slate-500"
                            id="small_size"
                            type="file"
                            onChange={(e) => {
                                const file = e.target.files?.[0]; // Access the first file from the FileList object
                                if (file) {
                                    setData({ ...data, image: file }); // Assign the file to the "avatar" field in the form data using setValue
                                }
                            }}></Input>
                    </div>
                    <div className="flex flex-col gap-2">
                        <label htmlFor="recipe" className="font-semibold">
                            Recipe
                        </label>
                        <MarkdownEditor value={data.recipe} className="h-56" id="recipe" placeholder="Describe the recipe..." onChange={(value) => setData({ ...data, recipe: value })} />
                    </div>
                    <Button status={savingStatus} variant={"dark"} className="w-fit" onClick={Save}>
                        {savingStatus === "loading" && "Editing"}
                        {savingStatus === "success" && "Edited"}
                        {savingStatus === "error" && "Error"}
                        {savingStatus === "none" && "Edit"}
                    </Button>
                </div>
            </Container>
        </>
    );
}
